import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Building, 
  Plus, 
  Search,
  FileText,
  ArrowRight,
  Calendar,
  Layers,
  X,
  Loader2,
  File,
  FileSpreadsheet,
  FileType
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  'Logistics': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Safety': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Financial': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Operations': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'HR': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Admin': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Project Management': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'IT': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'Business Development': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Engineering': 'bg-teal-500/20 text-teal-400 border-teal-500/30'
};

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userCategories, setUserCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [docSearchResults, setDocSearchResults] = useState([]);
  const [searchingDocs, setSearchingDocs] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDepartment, setNewDepartment] = useState({ name: '', description: '', category: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);
  
  // Debounced document search
  useEffect(() => {
    if (!docSearchQuery.trim()) {
      setDocSearchResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearchingDocs(true);
      try {
        const response = await axios.get(`${API}/documents/search?q=${encodeURIComponent(docSearchQuery)}`);
        setDocSearchResults(response.data.documents || []);
      } catch (error) {
        console.error('Document search failed:', error);
        setDocSearchResults([]);
      } finally {
        setSearchingDocs(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [docSearchQuery]);

  const fetchData = async () => {
    try {
      const [departmentsRes, categoriesRes, userCategoriesRes] = await Promise.all([
        axios.get(`${API}/departments`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/categories/user`)
      ]);
      setDepartments(departmentsRes.data);
      setCategories(categoriesRes.data.categories || []);
      setUserCategories(userCategoriesRes.data.categories || []);
      
      // Set default category for new department
      if (userCategoriesRes.data.categories?.length > 0) {
        setNewDepartment(prev => ({ ...prev, category: userCategoriesRes.data.categories[0] }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    if (!newDepartment.category) {
      toast.error('Please select a category');
      return;
    }
    setCreating(true);

    try {
      const response = await axios.post(`${API}/departments`, newDepartment);
      setDepartments([{ ...response.data, document_count: 0 }, ...departments]);
      setNewDepartment({ name: '', description: '', category: userCategories[0] || '' });
      setDialogOpen(false);
      toast.success('Department created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create department');
    } finally {
      setCreating(false);
    }
  };

  const filteredDepartments = departments.filter(dept => {
    const matchesSearch = dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dept.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || dept.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Departments</h1>
          <p className="text-muted-foreground mt-1">
            Manage your document collections by department
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-glow" data-testid="create-department-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Department
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Department</DialogTitle>
              <DialogDescription>
                Create a department to organize documents within a category.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateDepartment} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="department-name">Department Name</Label>
                <Input
                  id="department-name"
                  placeholder="e.g., Warehouse Operations"
                  value={newDepartment.name}
                  onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
                  required
                  data-testid="department-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="department-category" className="flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Category
                </Label>
                <Select 
                  value={newDepartment.category} 
                  onValueChange={(value) => setNewDepartment({ ...newDepartment, category: value })}
                >
                  <SelectTrigger data-testid="department-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {userCategories.map((category) => (
                      <SelectItem key={category} value={category}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${CATEGORY_COLORS[category]?.split(' ')[0] || 'bg-gray-500'}`} />
                          {category}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only users with access to this category can view this department
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="department-description">Description (Optional)</Label>
                <Textarea
                  id="department-description"
                  placeholder="Brief description of this department..."
                  value={newDepartment.description}
                  onChange={(e) => setNewDepartment({ ...newDepartment, description: e.target.value })}
                  rows={3}
                  data-testid="department-description-input"
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating || !newDepartment.category} data-testid="submit-department-btn">
                  {creating ? (
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Create Department'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search departments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-departments-input"
          />
        </div>
        
        {/* Global Document Search */}
        <div className="relative flex-1 max-w-md">
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search all documents..."
            value={docSearchQuery}
            onChange={(e) => setDocSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="search-all-documents-input"
          />
          {docSearchQuery && (
            <button
              onClick={() => setDocSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          
          {/* Document Search Results Dropdown */}
          {docSearchQuery && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
              {searchingDocs ? (
                <div className="p-4 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-2">Searching...</p>
                </div>
              ) : docSearchResults.length === 0 ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">No documents found</p>
                </div>
              ) : (
                <div className="py-2">
                  <p className="px-3 py-1 text-xs text-muted-foreground border-b border-border mb-1">
                    {docSearchResults.length} document(s) found
                  </p>
                  {docSearchResults.map((doc) => (
                    <Link
                      key={doc.id}
                      to={`/dashboard/departments/${doc.department_id}`}
                      onClick={() => setDocSearchQuery('')}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-secondary transition-colors"
                    >
                      {doc.filename?.endsWith('.pdf') ? (
                        <FileText className="w-4 h-4 text-red-400 flex-shrink-0" />
                      ) : doc.filename?.match(/\.(xlsx?|csv)$/) ? (
                        <FileSpreadsheet className="w-4 h-4 text-green-400 flex-shrink-0" />
                      ) : doc.filename?.match(/\.(docx?)$/) ? (
                        <FileType className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      ) : (
                        <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.filename}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {doc.department_name || 'Unknown Department'}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="category-filter-select">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              <SelectValue placeholder="All Departments" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {userCategories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Category Stats */}
      <div className="flex flex-wrap gap-2">
        {userCategories.map((category) => {
          const count = departments.filter(d => d.category === category).length;
          return (
            <button
              key={category}
              onClick={() => setCategoryFilter(categoryFilter === category ? 'all' : category)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                categoryFilter === category 
                  ? CATEGORY_COLORS[category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                  : 'bg-secondary/50 text-muted-foreground border-border hover:border-primary/30'
              }`}
            >
              {category} ({count})
            </button>
          );
        })}
      </div>

      {/* Departments Grid */}
      {filteredDepartments.length === 0 ? (
        <Card className="py-16">
          <CardContent className="text-center">
            <Building className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {searchQuery || categoryFilter !== 'all' ? 'No matching departments' : 'No departments yet'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {searchQuery || categoryFilter !== 'all'
                ? 'Try a different search term or category filter'
                : 'Create your first department to start uploading documents'
              }
            </p>
            {!searchQuery && categoryFilter === 'all' && (
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-create-department-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Department
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDepartments.map((dept) => (
            <Link
              key={dept.id}
              to={`/dashboard/departments/${dept.id}`}
              data-testid={`department-card-${dept.id}`}
            >
              <Card className="h-full card-hover group cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Building className="w-6 h-6 text-primary" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  
                  <div className="mb-3">
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${CATEGORY_COLORS[dept.category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}
                    >
                      {dept.category || 'Uncategorized'}
                    </Badge>
                  </div>
                  
                  <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {dept.name}
                  </h3>
                  
                  {dept.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {dept.description}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t border-border">
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {dept.document_count || 0} docs
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(dept.created_at)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
